<template>
	<BlogPostLayout
		page-title="How to Lock Individual Chats in GB WhatsApp"
		meta-description="Lock individual chats in GB WhatsApp with a PIN, password, pattern, or fingerprint to keep private conversations hidden from your main chat list."
		:image="poster">
		<p>GB WhatsApp allows some versions to protect individual conversations instead of locking the entire app. This is useful when you want to keep a specific personal or group chat away from the normal chat list.</p>
		<p>The exact menu can vary between GB WhatsApp versions, but the process is usually similar.</p>

		<h2>How to Lock a Chat in GB WhatsApp</h2>
		<p>Follow these steps to lock an individual conversation:</p>
		<ol>
			<li>
				Open
				<router-link :to="{ name: 'en-gb' }">
					<strong>GB WhatsApp</strong>
				</router-link>
				on your Android phone.
			</li>
			<li>Find the conversation you want to protect.</li>
			<li>
				<strong>Long-press the chat</strong>
				to select it.
			</li>
			<li>
				Tap the
				<strong>three-dot menu</strong>
				or the available chat options.
			</li>
			<li>
				Look for
				<strong>Lock Chat</strong>
				,
				<strong>Chat Lock</strong>
				, or a similar option.
			</li>
			<li>
				Choose a
				<strong>password, PIN, pattern, or fingerprint</strong>
				if prompted.
			</li>
			<li>Confirm the lock.</li>
		</ol>
		<p>Once enabled, the selected conversation may be removed from the regular chat list and placed inside a protected section.</p>

		<h2>Where Can I Find Locked Chats?</h2>
		<p>After locking a conversation, it may no longer appear in your normal chat list.</p>
		<p>Depending on your GB WhatsApp version, you may need to:</p>
		<ul>
			<li>
				Open the chat list and look for the
				<strong>Locked Chats</strong>
				section.
			</li>
			<li>Use the search area to access the protected section.</li>
			<li>Authenticate with your fingerprint, PIN, or password when prompted.</li>
		</ul>
		<p>The location and access method can differ between releases, so don't worry if your menu looks different from another GB WhatsApp version.</p>

		<h2>How to Unlock an Individual Chat</h2>
		<p>If you want the conversation to appear in your normal chat list again:</p>
		<ol>
			<li>
				Open the
				<strong>Locked Chats</strong>
				section.
			</li>
			<li>Verify your identity using the configured security method.</li>
			<li>Find the conversation you want to unlock.</li>
			<li>Open the chat options.</li>
			<li>
				Select
				<strong>Unlock Chat</strong>
				or the equivalent option.
			</li>
			<li>Confirm the change.</li>
		</ol>
		<p>The conversation should then return to the regular chat list.</p>

		<h2>What If You Cannot Find the Chat Lock Option?</h2>
		<p>Not every GB WhatsApp build uses the same menu or supports individual chat locking.</p>
		<p>
			If you cannot find
			<strong>Lock Chat</strong>
			, check the following:
		</p>
		<ul>
			<li>Make sure you are using a version that supports chat locking.</li>
			<li>Look under GB Settings for privacy-related options.</li>
			<li>Update to a compatible version if the feature is available in a newer release.</li>
			<li>Restart GB WhatsApp after changing privacy settings.</li>
		</ul>
		<p>Avoid installing another APK simply because the option is missing. Different builds can have different features and settings.</p>

		<h2>Does Chat Lock Hide the Conversation Completely?</h2>
		<p>Chat locking is designed to keep a selected conversation separate from the normal chat list, but the exact behavior depends on the GB WhatsApp version.</p>
		<p>Notifications may also behave differently depending on your Android settings and the privacy options provided by your build. If the conversation is sensitive, check your notification preview settings as well.</p>

		<h2>Can I Lock Group Chats?</h2>
		<p>
			Yes, supported versions can allow individual
			<strong>group conversations</strong>
			to be locked in the same way as personal chats.
		</p>
		<p>Select the group, open its available privacy or chat options, and look for the Lock Chat option.</p>

		<h2>What If I Forget the Chat Lock Password?</h2>
		<p>If you forget the PIN, password, or other authentication method used to protect a chat, check whether your GB WhatsApp version provides a recovery or reset option.</p>
		<p>
			<strong>Do not immediately clear app data or uninstall GB WhatsApp.</strong>
			Doing so can affect locally stored chats and media, especially if you do not have a usable backup.
		</p>
		<p>
			To lock an individual chat in GB WhatsApp,
			<strong>long-press the conversation -&gt; open the chat options -&gt; select Lock Chat -&gt; set your preferred security method</strong>
			. To access it later, open the Locked Chats section and authenticate.
		</p>
		<p>Because GB WhatsApp builds can differ, the exact name and location of the chat-lock option may vary between versions.</p>
	</BlogPostLayout>
</template>

<script>
import BlogPostLayout from '@/components/BlogPostLayout.vue'
import poster from '@/assets/lock-bg.png'

export default {
	name: 'LockIndividualChatsGuide',
	components: {
		BlogPostLayout,
	},
	data() {
		return {
			poster,
		}
	},
}
</script>
